import { followingSidebar } from '../../utils/sidebar.utils.js';
import {
  getSplitCandidates,
  shouldAddDividerBefore,
  shouldPreferLeftBalancedCandidate,
} from '../../utils/job-post-content-columns.utils.js';

const initializedSections = new WeakMap();
const desktopBreakpoint = 881;
const minStickyHeightDifference = 120;
const generatedDividerSelector = '[data-job-post-generated-divider]';

const getContentColumns = (section) => {
  const left = section.querySelector('[data-job-post-content-column="left"]');
  const right = section.querySelector('[data-job-post-content-column="right"]');

  if (!left || !right) {
    return null;
  }

  return { left, right };
};

const getContentBlocks = (column) => Array.from(column.children);

const getLayoutSignature = ({ left, right }) => [
  Math.round(left.getBoundingClientRect().height),
  Math.round(right.getBoundingClientRect().height),
  Math.round(left.parentElement?.parentElement?.getBoundingClientRect().width || 0),
].join(':');

const restoreContent = ({ left, right }) => {
  Array.from(right.childNodes).forEach((node) => left.append(node));
  left.querySelectorAll(`:scope > ${generatedDividerSelector}`).forEach((divider) => divider.remove());
};

const addGeneratedDividers = (column) => {
  getContentBlocks(column).forEach((block) => {
    if (!shouldAddDividerBefore(block)) {
      return;
    }

    const divider = document.createElement('hr');
    divider.setAttribute('data-job-post-generated-divider', 'true');
    block.before(divider);
  });
};

const moveBlocksToRightColumn = ({ left, right }, startIndex) => {
  let currentBlock = getContentBlocks(left)[startIndex];

  while (currentBlock) {
    const nextBlock = currentBlock.nextElementSibling;
    right.append(currentBlock);
    currentBlock = nextBlock;
  }
};

const restoreMeasuredBlocks = ({ left, right }) => {
  Array.from(right.children).forEach((block) => left.append(block));
};

const getBalancedSplitCandidate = (columns) => {
  const { candidates } = getSplitCandidates(getContentBlocks(columns.left));

  if (!candidates.length) {
    return null;
  }

  return candidates.reduce((balancedCandidate, candidate) => {
    moveBlocksToRightColumn(columns, candidate.index);

    const leftHeight = columns.left.getBoundingClientRect().height;
    const rightHeight = columns.right.getBoundingClientRect().height;
    const measuredCandidate = {
      candidate,
      leftHeight,
      rightHeight,
      heightDifference: Math.abs(leftHeight - rightHeight),
    };

    restoreMeasuredBlocks(columns);

    if (shouldPreferLeftBalancedCandidate(measuredCandidate, balancedCandidate)) {
      return measuredCandidate;
    }

    return balancedCandidate;
  }, null)?.candidate ?? null;
};

const splitJobPostContent = (section, state) => {
  const columns = getContentColumns(section);
  const container = section.querySelector('.job-post-sec__container');

  if (!columns || !container) {
    return;
  }

  container.classList.remove('job-post-sec__container--single-column');
  restoreContent(columns);
  addGeneratedDividers(columns.left);

  const splitCandidate = getBalancedSplitCandidate(columns);

  if (!splitCandidate) {
    container.classList.add('job-post-sec__container--single-column');
    state.layoutSignature = getLayoutSignature(columns);
    return;
  }

  moveBlocksToRightColumn(columns, splitCandidate.index);

  state.layoutSignature = getLayoutSignature(columns);
};

const destroySticky = (state) => {
  state.stickyController?.destroy();
  state.stickyController = null;
  state.activeColumn = null;
};

const getStickyItems = (section) =>
  Array.from(section.querySelectorAll('[data-job-post-sticky-column]'))
    .map((column) => ({
      column,
      inner: column.querySelector('[data-job-post-sticky-inner]'),
    }))
    .filter(({ inner }) => inner);

const getStickyTarget = (items) => {
  const measuredItems = items
    .map((item) => ({
      ...item,
      height: item.inner.getBoundingClientRect().height,
    }))
    .filter(({ height }) => height > 0);

  if (measuredItems.length < 2) {
    return null;
  }

  const sortedItems = measuredItems.sort((first, second) => first.height - second.height);
  const shortestItem = sortedItems[0];
  const tallestItem = sortedItems[sortedItems.length - 1];

  if (tallestItem.height - shortestItem.height < minStickyHeightDifference) {
    return null;
  }

  return shortestItem;
};

const updateJobPostSticky = (section, state) => {
  const container = section.querySelector('.job-post-sec__container');

  if (!container || window.innerWidth < desktopBreakpoint) {
    destroySticky(state);
    return;
  }

  const stickyTarget = getStickyTarget(getStickyItems(section));

  if (!stickyTarget) {
    destroySticky(state);
    return;
  }

  if (state.activeColumn !== stickyTarget.column) {
    destroySticky(state);

    state.activeColumn = stickyTarget.column;
    state.stickyController = followingSidebar({
      sidebar: stickyTarget.column,
      contentSticky: container,
      sidebarInnerSelector: '[data-job-post-sticky-inner]',
      bottomGap: 40,
    });

    return;
  }

  state.stickyController?.update();
};

const scheduleJobPostStickyUpdate = (section, state) => {
  if (state.updateFrame) {
    return;
  }

  state.updateFrame = requestAnimationFrame(() => {
    state.updateFrame = null;

    if (state.shouldSplitContent) {
      state.shouldSplitContent = false;
      splitJobPostContent(section, state);
    }

    updateJobPostSticky(section, state);
  });
};

const initJobPostSections = (scope = document) => {
  scope.querySelectorAll('.job-post-sec').forEach((section) => {
    const existingState = initializedSections.get(section);

    if (existingState) {
      existingState.shouldSplitContent = true;
      scheduleJobPostStickyUpdate(section, existingState);
      return;
    }

    const state = {
      activeColumn: null,
      resizeObserver: null,
      stickyController: null,
      updateFrame: null,
      layoutSignature: null,
      shouldSplitContent: true,
    };

    initializedSections.set(section, state);

    const scheduleUpdate = () => {
      state.shouldSplitContent = true;
      scheduleJobPostStickyUpdate(section, state);
    };

    const scheduleStickyUpdate = () => {
      const columns = getContentColumns(section);

      if (columns && state.layoutSignature !== getLayoutSignature(columns)) {
        state.shouldSplitContent = true;
      }

      scheduleJobPostStickyUpdate(section, state);
    };

    window.addEventListener('resize', scheduleUpdate);

    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(scheduleStickyUpdate);

      getStickyItems(section).forEach(({ inner }) => {
        state.resizeObserver.observe(inner);
      });
    }

    scheduleUpdate();
  });
};

initJobPostSections();
